/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */

"use client";

import { useState } from "react";
import { ResultActions } from "@/components/tools/result-actions";

type ShapeType = "slab" | "footing" | "column" | "stairs";

const shapes: { value: ShapeType; label: string }[] = [
  { value: "slab", label: "Slab / Patio / Sidewalk" },
  { value: "footing", label: "Wall Footing / Foundation" },
  { value: "column", label: "Round Column / Sonotube" },
  { value: "stairs", label: "Stairs" },
];

export function ConcreteCalculatorClient() {
  const [shape, setShape] = useState<ShapeType>("slab");
  // Slab
  const [slabLength, setSlabLength] = useState("");
  const [slabWidth, setSlabWidth] = useState("");
  const [slabThickness, setSlabThickness] = useState("4");
  // Footing
  const [footingLength, setFootingLength] = useState("");
  const [footingWidth, setFootingWidth] = useState("");
  const [footingDepth, setFootingDepth] = useState("12");
  // Column
  const [colDiameter, setColDiameter] = useState("12");
  const [colHeight, setColHeight] = useState("");
  const [colCount, setColCount] = useState("1");
  // Stairs
  const [stairWidth, setStairWidth] = useState("");
  const [stairRise, setStairRise] = useState("7.5");
  const [stairRun, setStairRun] = useState("11");
  const [stairCount, setStairCount] = useState("");
  const [stairThickness, setStairThickness] = useState("6");
  // Cost
  const [pricePerYard, setPricePerYard] = useState("");

  const wastePercent = 0.065; // 6.5% waste factor

  let cubicFeet = 0;

  if (shape === "slab") {
    const l = parseFloat(slabLength) || 0;
    const w = parseFloat(slabWidth) || 0;
    const t = (parseFloat(slabThickness) || 0) / 12; // inches to feet
    cubicFeet = l * w * t;
  } else if (shape === "footing") {
    const l = parseFloat(footingLength) || 0;
    const w = (parseFloat(footingWidth) || 0) / 12;
    const d = (parseFloat(footingDepth) || 0) / 12;
    cubicFeet = l * w * d;
  } else if (shape === "column") {
    const r = ((parseFloat(colDiameter) || 0) / 12) / 2; // inches to feet, then radius
    const h = parseFloat(colHeight) || 0;
    const n = parseFloat(colCount) || 1;
    cubicFeet = Math.PI * r * r * h * n;
  } else if (shape === "stairs") {
    const w = parseFloat(stairWidth) || 0;
    const rise = (parseFloat(stairRise) || 0) / 12;
    const run = (parseFloat(stairRun) || 0) / 12;
    const n = parseFloat(stairCount) || 0;
    const baseThickness = (parseFloat(stairThickness) || 0) / 12;
    // Each step is a triangle (rise * run / 2 * width) plus the base slab
    const stepsVolume = n * (rise * run / 2) * w;
    const totalRun = n * run;
    const baseVolume = totalRun * w * baseThickness;
    cubicFeet = stepsVolume + baseVolume;
  }

  const cubicYards = cubicFeet / 27;
  const cubicYardsWithWaste = cubicYards * (1 + wastePercent);
  const cubicYardsRounded = Math.ceil(cubicYardsWithWaste * 4) / 4; // round up to nearest 0.25
  const bags60lb = Math.ceil(cubicFeet / 0.45); // ~0.45 cu ft per 60lb bag
  const bags80lb = Math.ceil(cubicFeet / 0.6); // ~0.6 cu ft per 80lb bag
  const ppy = pricePerYard;
  const price = parseFloat(ppy) || 0;

  const hasResult = cubicFeet > 0;

  const fmt = (n: number) => n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const inputClass = "mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-foreground focus-visible:border-primary focus:outline-none focus-visible:ring-1 focus-visible:ring-primary";
  const labelClass = "block text-sm font-medium text-foreground";

  return (
    <div className="mt-8 rounded-lg card-premium p-6">
      <div className="mb-4">
        <label className={labelClass}>Shape</label>
        <div className="mt-1 flex flex-wrap gap-2">
          {shapes.map((s) => (
            <button
              key={s.value}
              onClick={() => setShape(s.value)}
              className={`rounded-md px-4 py-2 text-sm font-medium transition-colors ${shape === s.value ? "bg-foreground text-background" : "bg-muted text-muted-foreground hover:bg-muted/80"}`}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-3">
        {shape === "slab" && (
          <>
            <div>
              <label className={labelClass}>Length (ft)</label>
              <input type="number" value={slabLength} onChange={(e) => setSlabLength(e.target.value)} placeholder="20" className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>Width (ft)</label>
              <input type="number" value={slabWidth} onChange={(e) => setSlabWidth(e.target.value)} placeholder="10" className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>Thickness (inches)</label>
              <input type="number" value={slabThickness} onChange={(e) => setSlabThickness(e.target.value)} className={inputClass} />
            </div>
          </>
        )}
        {shape === "footing" && (
          <>
            <div>
              <label className={labelClass}>Length (ft)</label>
              <input type="number" value={footingLength} onChange={(e) => setFootingLength(e.target.value)} placeholder="100" className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>Width (inches)</label>
              <input type="number" value={footingWidth} onChange={(e) => setFootingWidth(e.target.value)} placeholder="20" className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>Depth (inches)</label>
              <input type="number" value={footingDepth} onChange={(e) => setFootingDepth(e.target.value)} className={inputClass} />
            </div>
          </>
        )}
        {shape === "column" && (
          <>
            <div>
              <label className={labelClass}>Diameter (inches)</label>
              <input type="number" value={colDiameter} onChange={(e) => setColDiameter(e.target.value)} className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>Height (ft)</label>
              <input type="number" value={colHeight} onChange={(e) => setColHeight(e.target.value)} placeholder="4" className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>Number of Columns</label>
              <input type="number" value={colCount} onChange={(e) => setColCount(e.target.value)} className={inputClass} />
            </div>
          </>
        )}
        {shape === "stairs" && (
          <>
            <div>
              <label className={labelClass}>Stair Width (ft)</label>
              <input type="number" value={stairWidth} onChange={(e) => setStairWidth(e.target.value)} placeholder="4" className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>Rise per Step (inches)</label>
              <input type="number" value={stairRise} onChange={(e) => setStairRise(e.target.value)} className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>Run per Step (inches)</label>
              <input type="number" value={stairRun} onChange={(e) => setStairRun(e.target.value)} className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>Number of Steps</label>
              <input type="number" value={stairCount} onChange={(e) => setStairCount(e.target.value)} placeholder="5" className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>Base Thickness (inches)</label>
              <input type="number" value={stairThickness} onChange={(e) => setStairThickness(e.target.value)} className={inputClass} />
            </div>
          </>
        )}
        <div>
          <label className={labelClass}>Price per Cubic Yard (optional)</label>
          <input type="number" value={pricePerYard} onChange={(e) => setPricePerYard(e.target.value)} placeholder="150" className={inputClass} />
        </div>
      </div>

      {hasResult && (
        <>
          <div className="mt-6">
            <div className="grid gap-4 rounded-lg bg-muted/50 p-4 sm:grid-cols-4">
              <div>
                <div className="text-sm text-muted-foreground">Volume</div>
                <div className="text-2xl font-bold text-foreground">{fmt(cubicFeet)} cu ft</div>
              </div>
              <div>
                <div className="text-sm text-muted-foreground">Cubic Yards (+ 6.5% waste)</div>
                <div className="text-2xl font-bold text-secondary">{cubicYardsRounded} yd&sup3;</div>
                <div className="text-xs text-muted-foreground">Exact: {fmt(cubicYardsWithWaste)} yd&sup3;</div>
              </div>
              <div>
                <div className="text-sm text-muted-foreground">Pre-Mixed Bags</div>
                <div className="text-lg font-bold text-foreground">{bags80lb} x 80lb</div>
                <div className="text-sm text-muted-foreground">or {bags60lb} x 60lb</div>
              </div>
              {price > 0 && (
                <div>
                  <div className="text-sm text-muted-foreground">Ready-Mix Cost</div>
                  <div className="text-2xl font-bold text-foreground">${fmt(cubicYardsRounded * price)}</div>
                </div>
              )}
            </div>
            {cubicYards > 1 && (
              <p className="mt-2 text-xs text-muted-foreground">For volumes over 1 yd&sup3;, ready-mix delivery is typically more cost-effective than bags.</p>
            )}
          </div>
          <ResultActions
            toolSlug="concrete-calculator"
            inputs={{ shape, slabLength, slabWidth, slabThickness, footingLength, footingWidth, footingDepth, colDiameter, colHeight, colCount, stairWidth, stairRise, stairRun, stairCount, stairThickness, pricePerYard }}
            outputs={{ cubicFeet, cubicYardsRounded, bags80lb, bags60lb, totalCost: price > 0 ? cubicYardsRounded * price : undefined }}
          />
        </>
      )}
    </div>
  );
}
